import { isEmpty } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  DO_NOT_SERIALIZE,
  type FilterItem,
  type FilterItemSource,
  type FilterViewModel,
} from '@/infra/FiltersViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Filter that selects one or more values from a list of options.
 */
export class SelectFilter<TKey, TValue>
  implements FilterViewModel<TKey, TValue[]>, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _parseStoredValue?: (value: unknown) => TValue | null

  /** @inheritDoc */
  key: TKey

  /** @inheritDoc */
  title: string

  /**
   * Where the options come from.
   */
  itemSource?: FilterItemSource<TValue>

  /** @inheritDoc */
  @observable accessor editing = false

  /**
   * Text the options are filtered by.
   */
  @observable accessor searchText = ''

  /**
   * Whether more than one value can be selected.
   */
  @observable accessor multi = false

  /**
   * The selected values.
   */
  @observable accessor selected: TValue[] = []

  /**
   * The options to select from.
   */
  @observable accessor options: FilterItem<TValue>[] = []

  /**
   * Loads the options from the item source.
   */
  fetchOptions = task(async (signal?: AbortSignal) => {
    if (this.itemSource) {
      this.setOptions(await this.itemSource(signal))
    }
  })

  constructor(
    key: TKey,
    title: string,
    itemSource?: FilterItemSource<TValue>,
    multi?: boolean,
    initialValue?: TValue[],
    parseStoredValue?: (value: unknown) => TValue | null,
  ) {
    this.key = key
    this.title = title
    this.multi = multi ?? false
    this.selected = initialValue ?? []
    this.options = []
    this.itemSource = itemSource
    this._parseStoredValue = parseStoredValue
  }

  /** @inheritDoc */
  @computed
  get valid() {
    return !isEmpty(this.value)
  }

  /** @inheritDoc */
  @computed
  get value() {
    return this.selected
  }

  /**
   * The options matching the search text.
   */
  @computed
  get searchFilterOptions() {
    if (this.searchText.length === 0) {
      return this.options.slice()
    }

    return this.options.filter(
      (option) =>
        option.title.toLowerCase().includes(this.searchText.toLowerCase()) ||
        option.description
          ?.toLowerCase()
          .includes(this.searchText.toLowerCase()),
    )
  }

  /**
   * Option titles by value.
   */
  @computed
  get optionsMap() {
    return new Map(this.options.map((o) => [o.value, o.title]))
  }

  /**
   * Loads the options, resolving once they have loaded or failed. The view
   * renders a failure from `fetchOptions`.
   */
  activate() {
    this._lifecycle.activate()
    return this.fetchOptions(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @computed
  get valueText() {
    return this.toString()
  }

  /**
   * Lists the selected titles, truncated to `limit` with a "+n selected"
   * suffix.
   *
   * @param limit
   */
  toString(limit?: number) {
    let items = this.selected.map((v) => this.optionsMap.get(v))

    if (limit && items.length > limit) {
      items = items.slice(0, limit - 1)

      const otherItemsCount = this.selected.length - items.length
      items.push(`${limit > 1 ? '+' : ''}${otherItemsCount} selected`)
    }

    return items.join(', ')
  }

  /**
   * Replaces the options.
   *
   * @param options
   */
  @action.bound
  setOptions(options: FilterItem<TValue>[]) {
    this.options = options
  }

  /**
   * Changes the search text.
   *
   * @param text
   */
  @action.bound
  changeSearchText(text: string) {
    this.searchText = text
  }

  /**
   * Replaces the selected values.
   *
   * @param val
   */
  @action.bound
  change(val: TValue[]) {
    this.selected = val
  }

  /** @inheritDoc */
  @action.bound
  clearValue() {
    this.selected = []
  }

  /**
   * Selects or deselects the value. A single-select filter only selects.
   *
   * @param val
   */
  @action.bound
  toggle(val: TValue) {
    if (this.multi) {
      this.change(
        this.isSelected(val)
          ? this.selected.filter((s) => s !== val)
          : [...this.selected, val],
      )
      return
    }

    if (!this.isSelected(val)) {
      this.selected = [val]
    }
  }

  /** @inheritDoc */
  @action.bound
  startEditing() {
    this.editing = true
  }

  /** @inheritDoc */
  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Clears the value and closes the editor.
   */
  @action.bound
  clear() {
    this.clearValue()
    this.stopEditing()
  }

  /**
   * Checks whether the value is selected.
   *
   * @param val
   */
  isSelected(val: TValue) {
    return this.selected.some((s) => s === val)
  }

  /** @inheritDoc */
  serialize() {
    return (
      JSON.stringify(
        this.options.filter((o) => this.selected.includes(o.value)),
      ) || DO_NOT_SERIALIZE
    )
  }

  /** @inheritDoc */
  deserialize(value: unknown) {
    // May behave unexpectedly if existing enum values change.
    try {
      if (typeof value === 'string') {
        const stored = JSON.parse(value || '{}') as FilterItem<unknown>[]
        const parse = this._parseStoredValue
        this.options = parse
          ? stored.flatMap((o) => {
              const parsed = parse(o.value)
              return parsed === null ? [] : [{ ...o, value: parsed }]
            })
          : (stored as FilterItem<TValue>[])
        this.selected = this.options.map((o) => o.value)
      }
    } catch {
      this.clearValue()
    }
  }
}
